import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useObs } from '../../contexts/ObsContext';
import { ProgressBar, Hint, Btn, SecPill, Spinner } from '../../components/ui';
import { toast } from 'react-toastify';
import { obsAPI } from '../../api';
import { BUCKETS, WHO_OPTIONS } from '../../theme';
import useRequireObs from '../../lib/useRequireObs';

const STEPS = ['Context', 'Evidence', 'Summary', 'Feedback'];

const WHO_LABEL = Object.fromEntries(WHO_OPTIONS.map(o => [o.value, o.label]));

/* ── One statement card ───────────────────────────────────────────────────── */
function StatementCard({ item, onChange, onRefresh }) {
  const [showSource, setShowSource]       = useState(false);
  const [editing, setEditing]             = useState(false);
  const [draft, setDraft]                 = useState(item.statement);
  const [strengthening, setStrengthening] = useState(false);

  const bucket = BUCKETS[item.bucket];
  const neg    = item.side === 'neg';
  const blocked = Boolean(item.blocked_reason);

  // Keep the editor in step if the statement changes underneath it.
  useEffect(() => { setDraft(item.statement); }, [item.statement]);

  const handleStrengthen = async () => {
    setStrengthening(true);
    try {
      const { data } = await obsAPI.strengthenImpact(item.note_id);
      if (data.is_draft) {
        toast.info(data.note || 'Could not strengthen this note automatically.');
      } else {
        toast.success('Impact strengthened — check it reads as you intended.');
        setShowSource(true);
        // The note changed, so the statement written from it is rebuilt.
        onRefresh?.();
      }
    } catch {
      toast.error('Could not reach the assistant. Please strengthen this note yourself.');
    } finally {
      setStrengthening(false);
    }
  };

  const [saving, setSaving] = useState(false);

  const saveEdit = async () => {
    const text = draft.trim();
    if (!text) { toast.error('A statement cannot be empty.'); return; }

    setSaving(true);
    try {
      const { data } = await obsAPI.updateStatement(item.id, text);
      onChange(data);
      setEditing(false);
    } catch (err) {
      console.error(err);
      toast.error('Could not save that statement. Your text has been kept.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`stmt-card${blocked ? ' blocked' : ''}`}>
      {item.is_draft && !blocked && (
        <div className="stmt-draft">
          Draft — written offline. Please read and edit before sending.
        </div>
      )}

      <div className="stmt-top">
        <span className={`stmt-side ${neg ? 'neg' : 'pos'}`}>
          {neg ? 'Could be more effective' : 'Most effective'}
        </span>
        {bucket && (
          <span className={`stmt-bucket ${neg ? 'neg' : 'pos'}`}>{bucket.name}</span>
        )}
        <span className="stmt-who">
          {item.student_identifier || WHO_LABEL[item.student_group] || ''}
        </span>
      </div>

      {item.blocked_reason ? (
        <div className="stmt-blocked">
          <div className="stmt-blocked-head">No statement yet</div>
          <p>{item.blocked_reason}</p>
          <div className="stmt-blocked-note">
            Correct the note on the evidence screen and this will build itself.
          </div>
        </div>
      ) : editing ? (
        <>
          <textarea
            className="fi stmt-edit"
            rows={6}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            autoFocus
          />
          <div className="stmt-edit-actions">
            <button className="stmt-btn primary" onClick={saveEdit} disabled={saving}>
              {saving ? 'Saving…' : 'Save statement'}
            </button>
            <button className="stmt-btn" onClick={() => { setDraft(item.statement); setEditing(false); }}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <p className="stmt-body">{item.statement}</p>
      )}

      {item.weakness && (
        <div className="stmt-weak">
          <div className="stmt-weak-head">Impact could be stronger</div>
          <p>{item.weakness.message}</p>
          <button className="stmt-weak-btn" onClick={handleStrengthen} disabled={strengthening}>
            {strengthening ? 'Working…' : 'Strengthen from the evidence'}
          </button>
          <div className="stmt-weak-note">
            Works only from what you recorded — it cannot add a quote or an observation.
          </div>
        </div>
      )}

      {!editing && (
        <div className="stmt-actions">
          {!item.blocked_reason && (
            <button className="stmt-btn" onClick={() => setEditing(true)}>Edit statement</button>
          )}
          <button
            className={`stmt-btn${showSource ? ' on' : ''}`}
            onClick={() => setShowSource(s => !s)}
          >
            {showSource ? 'Hide source notes' : 'Show source notes'}
          </button>
        </div>
      )}

      {showSource && (
        <div className="stmt-source">
          <div className="stmt-source-lbl">Evidence note</div>
          <p>{item.evidence_text || <em>None recorded.</em>}</p>
          <div className="stmt-source-lbl">Impact note</div>
          <p>{item.impact_text || <em>None recorded.</em>}</p>
        </div>
      )}
    </div>
  );
}

/* ── Page ─────────────────────────────────────────────────────────────────── */
export default function ConclusionsPage() {
  const navigate = useNavigate();
  const { obs, setConclusions: setCtxConclusions, setStatements } = useObs();
  useRequireObs();

  const [items, setItems]     = useState([]);
  const [building, setBuilding] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  // Statements are generated for every note carried forward, not just the
  // selected ones, so the observer sees the whole picture before deciding
  // what to lead with.
  // Statements are built on the server only. If it cannot be reached there is
  // nothing to show — the summary could not be saved without it either — so
  // the observer is told, and can try again.
  const load = useCallback(async () => {
    setBuilding(true);
    try {
      const { data } = await obsAPI.statements(obs?.id);
      setItems(data.statements);
      setLoadError('');
    } catch (err) {
      setItems([]);
      setLoadError(err?.response?.data?.detail ||
        'Could not build the summary. Check your connection and try again.');
    } finally {
      setBuilding(false);
    }
  }, [obs?.id]);

  useEffect(() => { load(); }, [load]);

  const updateItem = useCallback((updated) => {
    setItems(list => list.map(i => (i.id === updated.id ? updated : i)));
  }, []);

  const best   = items.filter(i => i.side === 'pos');
  const better = items.filter(i => i.side === 'neg');

  const save = async (nextRoute) => {
    // Nothing to save, and nothing for the next screen to work from.
    if (items.length === 0) {
      toast.error(loadError || 'There are no statements to save yet.');
      return;
    }
    // Every note was declined — there is nothing to take into a debrief.
    if (!items.some(i => i.statement)) {
      toast.error('None of these notes could be made into a statement yet. '
                  + 'Correct them on the evidence screen first.');
      return;
    }
    const text = items.filter(i => i.statement)
                      .map(i => i.statement).join('\n\n');
    setCtxConclusions(text);
    // Carry the statements themselves forward so the debrief can say them
    // word for word — nothing is paraphrased after this point.
    setStatements(items);
    setLoading(true);
    try {
      await obsAPI.update(obs?.id, { status: 'debrief_pending', conclusions_text: text });
      toast.success('Summary saved.');
      navigate(nextRoute);
    } catch (error) {
      console.error(error);
      toast.error('Could not save the summary. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="screen">
      <div className="obsbar">
        <button className="obsbar-back" onClick={() => navigate('/observe/evidence')}>← Back</button>
        <div className="obsbar-title">Summary of findings</div>
      </div>
      <ProgressBar steps={STEPS} current={2} />

      <div className="scroll">
        <div className="pad">
          <div className="stmt-intro">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M8 1.8l1.7 3.9 4.2.4-3.2 2.8 1 4.1L8 10.9l-3.7 2.1 1-4.1L2.1 6.1l4.2-.4L8 1.8z"
                    stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
            </svg>
            <p>
              Generated from your evidence and impact notes, trained on the ELO statement
              set. Read each one and edit anything that does not sound like you.
            </p>
          </div>

          {building ? (
            <div style={{ display:'flex', justifyContent:'center', padding:'32px 0' }}>
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <div className="dash-empty">
              {loadError || 'No evidence notes were recorded for this observation.'}
              {loadError && (
                <div style={{ marginTop: 10 }}>
                  <button className="stmt-btn" onClick={load}>Try again</button>
                </div>
              )}
            </div>
          ) : (
            <>
              {best.length > 0 && (
                <>
                  <SecPill bg="var(--teal-l)" color="var(--teal-d)">Learning was most effective for…</SecPill>
                  {best.map(item => (
                    <StatementCard key={item.id} item={item} onChange={updateItem} onRefresh={load} />
                  ))}
                </>
              )}

              {better.length > 0 && (
                <>
                  <SecPill bg="var(--purple-l)" color="var(--purple-d)">Learning could have been more effective for…</SecPill>
                  {better.map(item => (
                    <StatementCard key={item.id} item={item} onChange={updateItem} onRefresh={load} />
                  ))}
                </>
              )}
            </>
          )}

          <Hint style={{ marginTop: 12 }}>
            Next you'll select the highest leverage findings for the debrief before meeting with the teacher.
          </Hint>

          <Btn variant="blue" onClick={() => save('/observe/leverage')} loading={loading} style={{ marginBottom: 8 }}>
            Save &amp; select debrief focus →
          </Btn>
          <Btn variant="outline" onClick={() => save('/')} style={{ fontSize: 12 }}>
            Debrief pending — schedule later
          </Btn>
        </div>
      </div>
    </div>
  );
}
